import React, { useState, useContext, useEffect } from 'react';
import { AppContext } from './App.js';
import { getAuth, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { ArrowLeft, Check, Shield, Zap, Loader2, ArrowRight, AlertCircle } from 'lucide-react';

const SignUpPage = () => {
    const { purchaseIntent, setCurrentPage, setPurchaseIntent } = useContext(AppContext);
    
    const [step, setStep] = useState(purchaseIntent ? 'account' : 'plan');
    const [selectedPlan, setSelectedPlan] = useState(purchaseIntent || 'demo');
    
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (purchaseIntent) {
            setSelectedPlan(purchaseIntent);
            setStep('account');
        }
    }, [purchaseIntent]);

    const handlePlanSelect = (planId) => {
        setSelectedPlan(planId);
        setPurchaseIntent(planId); 
        setStep('account');
        setError('');
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const auth = getAuth();
        try {
            // 1. Set localStorage flags BEFORE creating the account so onAuthStateChanged
            //    in AppProvider reads them the moment it fires after signup.
            if (selectedPlan === 'demo') {
                localStorage.setItem('pendingDemoUser', 'true');
            } else {
                localStorage.setItem('pendingPurchaseUser', 'true');
            }

            // 2. Create User
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            
            // 3. Add Name
            if (fullName) {
                await updateProfile(userCredential.user, { displayName: fullName });
            }

            // 3b. Outreach attribution. If this visitor arrived from a BLACKBOX
            //     outreach email (?bb_ref=... captured by the script in
            //     index.html), report which email produced this signup, with
            //     the new Firebase uid as the join key. Does nothing for
            //     visitors who didn't come from an email. Fire-and-forget:
            //     never blocks or breaks signup.
            try {
                window.bbAttribution?.signup({ userId: userCredential.user.uid, plan: selectedPlan });
            } catch (attrErr) {
                console.warn('Outreach attribution skipped:', attrErr);
            }

            // 4. Route based on plan.
            //    Paid users go to checkout.
            //    Demo users: AppProvider's onAuthStateChanged picks up pendingDemoUser,
            //    calls initializeDemoUser, then redirects to 'projects' once claims are set.
            if (selectedPlan === 'indie' || selectedPlan === 'pro') {
                setCurrentPage('checkout');
            } else {
                setCurrentPage('projects');
            }
            
        } catch (err) {
            console.error("Signup Error:", err);
            // Clear the flags if account creation failed
            localStorage.removeItem('pendingDemoUser');
            localStorage.removeItem('pendingPurchaseUser');
            let msg = err.message;
            if (msg.includes('email-already-in-use')) msg = 'This email is already registered. Please log in.';
            if (msg.includes('weak-password')) msg = 'Password should be at least 6 characters.';
            setError(msg);
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-zinc-950 text-zinc-100 font-sans selection:bg-purple-500/30 selection:text-purple-200 relative overflow-hidden flex flex-col">
            
            {/* Background Atmosphere */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] bg-purple-900/10 blur-[120px] rounded-full pointer-events-none"></div>

            {/* Header / Nav */}
            <div className="relative z-10 px-6 py-6 flex justify-between items-center max-w-7xl mx-auto w-full">
                <button 
                    onClick={() => step === 'account' ? setStep('plan') : setCurrentPage('landing')}
                    className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-sm font-medium"
                >
                    <ArrowLeft size={16} />
                    {step === 'account' ? 'Back to Plans' : 'Back to Home'}
                </button>
                <div className="text-xl font-medium tracking-tight">
                    BLACKBOX<span className="text-zinc-600">cr</span>
                </div>
                <div className="w-20"></div>
            </div>

            {/* Main Content Area */}
            <div className="relative z-10 flex-1 flex flex-col items-center justify-center p-6">
                
                {step === 'plan' ? (
                    <div className="w-full max-w-5xl">
                        <div className="text-center mb-12">
                            <h1 className="text-3xl md:text-5xl font-semibold mb-4 tracking-tight">Select your loadout.</h1>
                            <p className="text-zinc-400 text-lg">Choose the capacity that fits your production.</p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6">
                            <PlanCard 
                                title="Free Trial"
                                price="$0"
                                description="7 days of full access."
                                features={['720p Exports', 'Watermarked', 'All Features', 'Email Support']}
                                onClick={() => handlePlanSelect('demo')}
                                active={selectedPlan === 'demo'}
                            />
                            <PlanCard 
                                title="Indie Project"
                                price="$199"
                                period="/ film"
                                description="For the festival circuit."
                                features={['1080p Exports', 'No Watermark', 'H.264 / .mp4', '90 Days Storage']}
                                onClick={() => handlePlanSelect('indie')}
                                active={selectedPlan === 'indie'}
                                highlight
                            />
                            <PlanCard 
                                title="Feature Film"
                                price="$399"
                                period="/ film"
                                description="For theatrical release."
                                features={['4K Resolution', 'ProRes 4444 + Alpha', 'NLE Data Export', 'Priority Support']}
                                onClick={() => handlePlanSelect('pro')}
                                active={selectedPlan === 'pro'}
                                glow
                            />
                        </div>
                    </div>
                ) : (
                    <div className="w-full max-w-md">
                        <div className="bg-zinc-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl relative">
                            
                            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-zinc-950 border border-zinc-800 px-4 py-1.5 rounded-full flex items-center gap-2 shadow-xl">
                                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                                <span className="text-xs font-medium text-zinc-300">
                                    Selected: <span className="text-white uppercase">{selectedPlan}</span>
                                </span>
                            </div>

                            <div className="text-center mb-8 mt-2">
                                <h2 className="text-2xl font-semibold mb-2">Create Access ID</h2>
                                <p className="text-zinc-500 text-sm">Initialize your workspace to begin.</p>
                            </div>

                            <form onSubmit={handleSignup} className="space-y-4">
                                {error && (
                                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-start gap-3 text-red-400 text-sm">
                                        <AlertCircle size={16} className="mt-0.5 shrink-0" />
                                        <span>{error}</span>
                                    </div>
                                )}

                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-zinc-400 ml-1">Full Name</label>
                                    <input 
                                        type="text" 
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        className="w-full bg-zinc-950/50 border border-zinc-800 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:border-zinc-600 focus:ring-1 focus:ring-zinc-600 transition-all"
                                        placeholder="John Jones"
                                    />
                                </div>

                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-zinc-400 ml-1">Email</label>
                                    <input 
                                        type="email" 
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full bg-zinc-950/50 border border-zinc-800 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:border-zinc-600 focus:ring-1 focus:ring-zinc-600 transition-all"
                                        placeholder="director@studio.com"
                                        required
                                    />
                                </div>

                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-zinc-400 ml-1">Password</label>
                                    <input 
                                        type="password" 
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full bg-zinc-950/50 border border-zinc-800 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:border-zinc-600 focus:ring-1 focus:ring-zinc-600 transition-all"
                                        placeholder="••••••••"
                                        required
                                    />
                                </div>

                                <button 
                                    type="submit" 
                                    disabled={loading}
                                    className="w-full bg-white text-black hover:bg-zinc-200 font-medium rounded-lg py-3 mt-4 transition-all flex items-center justify-center gap-2"
                                >
                                    {loading ? <Loader2 size={18} className="animate-spin"/> : 'Initialize Workspace'}
                                    {!loading && <ArrowRight size={16} />}
                                </button>
                            </form>

                            <div className="mt-6 pt-6 border-t border-white/5 flex items-center justify-center gap-4 text-[10px] text-zinc-600 uppercase tracking-widest">
                                <div className="flex items-center gap-1"><Shield size={12}/> SSL Encrypted</div>
                                <div className="flex items-center gap-1"><Zap size={12}/> Instant Access</div>
                            </div>
                        </div>
                        
                        <p className="text-center mt-6 text-sm text-zinc-500">
                            Already have an account? <button onClick={() => setCurrentPage('login')} className="text-white hover:text-purple-400 underline decoration-zinc-700 underline-offset-4">Login</button>
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

const PlanCard = ({ title, price, period, description, features, onClick, active, highlight, glow }) => (
    <div 
        onClick={onClick}
        className={`
            relative p-8 rounded-2xl border cursor-pointer group transition-all duration-300
            ${active ? 'bg-zinc-900 border-white ring-1 ring-white/20' : 'bg-zinc-950 border-white/10 hover:border-zinc-700 hover:bg-zinc-900/50'}
            ${glow ? 'shadow-[0_0_50px_-12px_rgba(168,85,247,0.15)]' : ''}
        `}
    >
        {highlight && (
            <div className="absolute -top-3 left-6 bg-zinc-100 text-zinc-950 text-[10px] font-bold px-2 py-0.5 rounded tracking-wide uppercase">
                Recommended
            </div>
        )}
        
        <div className="flex justify-between items-start mb-6">
            <div>
                <h3 className={`text-lg font-medium ${active ? 'text-white' : 'text-zinc-300'}`}>{title}</h3>
                <p className="text-zinc-500 text-sm mt-1">{description}</p>
            </div>
            {active && <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center"><Check size={14} className="text-black"/></div>}
        </div>

        <div className="mb-6">
            <span className="text-4xl font-semibold text-white">{price}</span>
            {period && <span className="text-zinc-600 ml-1 text-sm">{period}</span>}
        </div>

        <ul className="space-y-3">
            {features.map((feat, i) => (
                <li key={i} className="flex items-center gap-3 text-sm text-zinc-400">
                    <Check size={14} className={active || glow ? "text-purple-400" : "text-zinc-600"} />
                    {feat}
                </li>
            ))}
        </ul>

        <div className={`mt-8 w-full py-2.5 rounded-lg border text-sm font-medium text-center transition-colors ${
            active 
            ? 'bg-white border-white text-black' 
            : 'border-zinc-800 text-zinc-400 group-hover:border-zinc-600 group-hover:text-white'
        }`}>
            {active ? 'Selected' : 'Select Plan'}
        </div>
    </div>
);

export default SignUpPage;